import { useState, useEffect } from "react";
import { AnimatePresence } from "motion/react";
import { IntroScreen } from "./components/IntroScreen";
import { QuestionScreen } from "./components/QuestionScreen";
import { ResultScreen } from "./components/ResultScreen";
import { ProductRecommendationScreen } from "./components/ProductRecommendationScreen";
import Analytics from "../../utils/analytics";

/* const pages = [
  { persona: "livsnyderen", pageurl: "/asbjoern/kp/uanset-hvilken-asbjoern-du-er/livsnyderen", splittesturl: "https://www.ase.dk/inspiration/kp/hvilken-type-er-du/livsnyderen", produktpackage: "akasse|fagforening", alternateProductPackage: "akasse|fagforening", campaign: "Test Livsnyderen", type: "employee", step: "1" },
  { persona: "redebyggeren", pageurl: "/asbjoern/kp/uanset-hvilken-asbjoern-du-er/redebyggeren", splittesturl: "https://www.ase.dk/inspiration/kp/hvilken-type-er-du/redebyggeren", produktpackage: "akasse|fagforeningloensikring", alternateProductPackage: "akasse|fagforening", campaign: "Test Redebyggeren", type: "employee", step: "1" },
  { persona: "den selvkørende", pageurl: "/asbjoern/kp/uanset-hvilken-asbjoern-du-er/den-selvkoerende", splittesturl: "https://www.ase.dk/inspiration/kp/hvilken-type-er-du/den-selvkoerende", produktpackage: "akasse", alternateProductPackage: "akasse", campaign: "Test Den selvkørende", type: "employee", step: "1" },
  { persona: "den forberedte", pageurl: "/asbjoern/kp/uanset-hvilken-asbjoern-du-er/den-forberedte", splittesturl: "https://www.ase.dk/inspiration/kp/hvilken-type-er-du/den-forberedte", produktpackage: "akasse|fagforening|loensikring", alternateProductPackage: "akasse|fagforening", campaign: "Test Den forberedte", type: "employee", step: "1" },
]; */

// Question data
const questions = [
  {
    id: 1,
    question: "Hvordan vil du beskrive din hverdag?",
    answers: [
      { id: "a1", text: "Den er planlagt og der er styr på tingene.", values: ["prepared"] },
      { id: "a2", text: "Jeg prioriterer ansvar og omsorg for familien højt.", values: ["family"] },
      { id: "a3", text: "Jeg tager ansvar og driver selv tingene fremad dagligt.", values: ["selfDriving"] },
      { id: "a4", text: "Jeg nyder de små øjeblikke og tager tingene roligt.", values: ["enjoyer"] },
    ],
  },
  {
    id: 2,
    question: "Hvad motiverer dig mest i dit arbejdsliv?",
    answers: [
      { id: "b1", text: "Jeg motiveres af overblik og at være godt forberedt.", values: ["prepared"] },
      { id: "b2", text: "Jeg motiveres af tryghed og ansvar for andre dagligt.", values: ["family"] },
      { id: "b3", text: "Jeg motiveres af frihed og professionel udvikling dagligt.", values: ["selfDriving"] },
      { id: "b4", text: " Jeg motiveres af gode oplevelser dagligt.", values: ["enjoyer"] },
    ],
  },
  {
    id: 3,
    question: "Hvordan reagerer du, når der opstår noget uventet på jobbet?",
    answers: [
      { id: "c1", text: "Jeg overvejer muligheder og vælger den mest sikre løsning.", values: ["prepared"] },
      { id: "c2", text: "Jeg tager hensyn til andre og forsøger at skabe stabilitet.", values: ["family"] },
      { id: "c3", text: "Jeg handler hurtigt og tager ansvar for at løse situationen.", values: ["selfDriving"] },
      { id: "c4", text: "Jeg tager det roligt og lærer af situationen.", values: ["enjoyer"] },
    ],
  },
  {
    id: 4,
    question: "Hvordan ser din indtægt typisk ud i løbet af en måned?",
    answers: [
      { id: "d1", text: "Jeg ligger under 29.000 kr. før skat.", values: ["under 29000"] },
      { id: "d2", text: "Jeg ligger over 29.000 kr. før skat.", values: ["over 29000"] },
    ],
  },
  {
    id: 5,
    question: "Hvordan prioriterer du din økonomi?",
    answers: [
      { id: "e1", text: "Jeg planlægger fremad og sikrer mig mod uventede udgifter.", values: ["prepared"] },
      { id: "e2", text: "Jeg prioriterer stabil økonomi, der fungerer for familien.", values: ["family"] },
      { id: "e3", text: "Jeg investerer i mig selv og min karriere.", values: ["selfDriving"] },
      { id: "e4", text: "Jeg prioriterer oplevelser, nydelse og det gode liv.", values: ["enjoyer"] },
    ],
  },
  {
    id: 6,
    question: "Hvilken type støtte har du mest brug for?",
    answers: [
      { id: "f1", text: "Jeg har brug for rådgivning og hjælp til at være godt forberedt.", values: ["prepared"] },
      { id: "f2", text: "Jeg har brug for hjælp til ansvar og balance.", values: ["family"] },
      { id: "f3", text: "Jeg har brug for sparring til udvikling og selvstændighed.", values: ["selfDriving"] },
      { id: "f4", text: "Jeg har brug for støtte til frihed og overskud.", values: ["enjoyer"] },
    ],
  },
  {
    id: 7,
    question: "Hvad er dit vigtigste fokus det næste år?",
    answers: [
      { id: "g1", text: "At stå stærkt og være forberedt på forskellige scenarier.", values: ["prepared"] },
      { id: "g2", text: "At skabe mere ro og stabilitet i hverdagen.", values: ["family"] },
      { id: "g3", text: "At tage næste skridt i karrieren.", values: ["selfDriving"] },
      { id: "g4", text: "At udvikle mig og finde min retning i livet.", values: ["enjoyer"] },
    ],
  },
];

// Persona mapping
const personas = {
  enjoyer: "livsnyderen",
  family: "redebyggeren",
  selfDriving: "den selvkørende",
  prepared: "den forberedte",
};

type Screen = "intro" | "questions" | "result" | "productRecommendation";

type PersonaPage = {
  persona: string;
  pageUrl: string;
  produktpackage: string;
  alternateProductPackage: string;
  campaign: string;
  type: string;
  step: string;
};

export default function PersonalityTest() {
  const [screen, setScreen] = useState<Screen>("intro");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<string[][]>([]);
  const [persona, setPersona] = useState<string>("");
  const [salaryInsurance, setSalaryInsurance] = useState<boolean>(false);
  const [urlEnjoyer, setUrlEnjoyer] = useState<string>("");
  const [urlSelfDriving, setUrlSelfDriving] = useState<string>("");
  const [urlPrepared, setUrlPrepared] = useState<string>("");
  const [urlNester, setUrlNester] = useState<string>("");


  const pages: PersonaPage[] = [
    { persona: "livsnyderen", pageUrl: urlEnjoyer, produktpackage: "akasse|fagforening", alternateProductPackage: "akasse|fagforening", campaign: "Test Livsnyderen", type: "employee", step: "1" },
    { persona: "redebyggeren", pageUrl: urlNester, produktpackage: "akasse|fagforeningloensikring", alternateProductPackage: "akasse|fagforening", campaign: "Test Redebyggeren", type: "employee", step: "1" },
    { persona: "den selvkørende", pageUrl: urlSelfDriving, produktpackage: "akasse", alternateProductPackage: "akasse", campaign: "Test Den selvkørende", type: "employee", step: "1" },
    { persona: "den forberedte", pageUrl: urlPrepared, produktpackage: "akasse|fagforening|loensikring", alternateProductPackage: "akasse|fagforening", campaign: "Test Den forberedte", type: "employee", step: "1" },
  ];

  //Get url for resultpage
  useEffect(() => {
    const fetchUrl = async () => {
      try {
        const enjoyerUrl = form?.dataset.urlEnjoyer;
        const selfDrivingUrl = form?.dataset.urlSelfdriving;
        const preparedUrl = form?.dataset.urlPrepared;
        const nesterUrl = form?.dataset.urlNester;
        if (enjoyerUrl) {
          setUrlEnjoyer(enjoyerUrl);
        }
        if (selfDrivingUrl) {
          setUrlSelfDriving(selfDrivingUrl);
        }
        if (preparedUrl) {
          setUrlPrepared(preparedUrl);
        }
        if (nesterUrl) {
          setUrlNester(nesterUrl);
        }
      } catch (err) {}
    };

    const form = document.querySelector(`[data-component='personality-test']`) as HTMLElement;

    fetchUrl();
  }, []); 

  const handleStart = () => {
    // track test started event
    Analytics.getInstance().trackPersonaTest("start", "start");
    setScreen("questions");
    setCurrentQuestionIndex(0);
    setAnswers([]);
    scrollToTop();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToBottom = () => {
    const footer = document.getElementsByTagName("footer");
    
    setTimeout(() => {
      const container = document.getElementById("personalityTest");
      if (!container) return;
      window.scrollTo({
          top: (container.scrollHeight - footer[0]?.clientHeight),
          behavior: "smooth",
        });
      }, 1500);
  };

  const handleAnswer = (answerId: string) => {
    // Find the selected answer to get all its values
    const selectedAnswer = questions[currentQuestionIndex].answers.find((a) => a.values[0] === answerId);
    const answerValues = selectedAnswer ? selectedAnswer.values : [answerId];

    const newAnswers = [...answers, answerValues];
    setAnswers(newAnswers);

    // Move to next question or show result
    if (currentQuestionIndex < questions.length - 1) {
      // track question answered event
      let curr = currentQuestionIndex + 1;
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      if (selectedAnswer?.id === "d1") {
        setSalaryInsurance(false);
        console.log("user does not need salary insurance");
      }
      if (selectedAnswer?.id === "d2") {
        setSalaryInsurance(true);
        console.log("user needs salary insurance");
      }
      Analytics.getInstance().trackPersonaTest("q" + curr, selectedAnswer?.values.join(",") || "");
      scrollToBottom();
    } else {
      // track test completed event
      Analytics.getInstance().trackPersonaTest("q" + (currentQuestionIndex + 1), selectedAnswer?.values.join(",") || "");
      // wait for answers to be set
      // Calculate persona based on most common answer type
      // Flatten all answer values and count occurrences
      const allValues = newAnswers.flat();
      const counts = allValues.reduce((acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const dominantType = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      setPersona(personas[dominantType as keyof typeof personas]);
      Analytics.getInstance().trackPersonaTest("result", personas[dominantType as keyof typeof personas]);
      setScreen("result");
      //setScreen("productRecommendation");
      scrollToTop();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      let ans = answers.slice(0, -1);
      setAnswers(ans);
    }
  };

  const handleRestart = () => {
    // track restart event
    console.log("tracking restarted the personality test");
    setScreen("intro");
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setPersona("");
    scrollToTop();
  };

  const handleProductRecommendation = () => {
    // track recommendation click
    console.log("tracking clicked on product recommendation");
    setScreen("productRecommendation");
    scrollToTop();
  };

  const handleMembershipSignUp = () => {
    // track membership sign-up click
    // create OI link with products based on persona
    let url = "/indmeldelse";
    url += "?produkt=" + (!salaryInsurance ? pages.find((page) => page.persona === persona)?.alternateProductPackage : pages.find((page) => page.persona === persona)?.produktpackage) || "";
    url += "&campaign=" + pages.find((page) => page.persona === persona)?.campaign || "";
    url += "&type=" + pages.find((page) => page.persona === persona)?.type || "";
    if (pages.find((page) => page.persona === persona)?.step == "2") {
      url += "&step=" + pages.find((page) => page.persona === persona)?.step || "";
    }

    window.open(url);
  };

  const handleMembershipRecomendation = () => {
    // track membership recommendation click
    // create OI link with products based on persona
    
    const selectedPage = pages.find((page) => page.persona === persona);
    /* let url = (Math.random() < 0.5 ? selectedPage?.pageurl : selectedPage?.splittesturl) || selectedPage?.pageurl || selectedPage?.splittesturl || "/indmeldelse"; */
  
    let url = selectedPage?.pageUrl || "/indmeldelse";
    url += "?produkt=" + (!salaryInsurance ? pages.find((page) => page.persona === persona)?.alternateProductPackage : pages.find((page) => page.persona === persona)?.produktpackage) || "";
    url += "&campaign=" + pages.find((page) => page.persona === persona)?.campaign || "";
    url += "&type=" + pages.find((page) => page.persona === persona)?.type || "";
    if (pages.find((page) => page.persona === persona)?.step == "2") {
      url += "&step=" + pages.find((page) => page.persona === persona)?.step || "";
    }
    Analytics.getInstance().trackPersonaTestCta("cta_button", persona, url, "Persona anbefaling");
    window.open(url);
  };

  const shareOnFB = () => {
    const url = encodeURIComponent(window.location.href);
    const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    Analytics.getInstance().trackTestSharer("personalityTest_asbjoern", fbShareUrl, "Facebook");
    window.open(fbShareUrl, "_blank");
  };

  return (
    <div id="testContainer" className="bg-bg-soft">
      <AnimatePresence mode="wait">
        {screen === "intro" && <IntroScreen key="intro" onStart={handleStart} />}

        {screen === "questions" && <QuestionScreen key="questions" currentQuestion={currentQuestionIndex + 1} totalQuestions={questions.length} question={questions[currentQuestionIndex]} onAnswer={handleAnswer} onPrevious={handlePrevious} canGoBack={currentQuestionIndex > 0} />}

        {screen === "result" && <ResultScreen key="result" persona={persona} onRestart={handleRestart} onProductRecommendation={handleMembershipRecomendation} sharer={shareOnFB} />}

        {screen === "productRecommendation" && <ProductRecommendationScreen key="productRecommendation" persona={persona} onRestart={handleRestart} onMembershipRecomendation={handleMembershipRecomendation} onMembershipSignup={handleMembershipSignUp} sharer={shareOnFB} />}
      </AnimatePresence>
    </div>
  );
}
